import type { Ref } from "vue";
import type { SuggestionItem, SearchZone } from "./docSearch/types";

export interface SearchResult {
  id: string;
  title: string;
  titles: string[];
  content?: string;
  level: number;
  zone?: SearchZone;
}

export interface SearchProvider {
  isLoading: Ref<boolean>;
  searchTerm: Ref<string>;
  placeholder: string;
  suggestions: Ref<SuggestionItem[]>;
  filteredResults: Ref<SearchResult[]>;
  load: () => void;
  resolveUrl: (id: string) => string;
}

declare module "#app" {
  interface NuxtApp {
    _searchProvider?: Ref<SearchProvider | null>;
  }
}

// The active provider lives on the Nuxt app instance, not in module scope.
// Module-level state is shared across every SSR request in a worker — a
// cross-request leak. `useNuxtApp()` is request-scoped on the server and a
// single instance on the client, so this is the right place for it. The
// header's <SearchOverlay> and any layout that overrides the provider all
// resolve the same ref within a request.
function registry(): Ref<SearchProvider | null> {
  const nuxt = useNuxtApp();
  if (!nuxt._searchProvider) {
    nuxt._searchProvider = shallowRef<SearchProvider | null>(null);
  }
  return nuxt._searchProvider as Ref<SearchProvider | null>;
}

export function provideSearchProvider(provider: SearchProvider) {
  registry().value = provider;
}

export function useSearchProvider(): Ref<SearchProvider | null> {
  return registry();
}
